import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  ChevronLeft, Search, Loader2, CheckCircle2, AlertCircle,
  QrCode, Building2, Copy, Clock, RefreshCw,
  Zap, Receipt, User, FileText,
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { PaymentBadges } from '../components/PaymentBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_METHODS = [
  { id: 'qris', name: 'QRIS', icon: QrCode, channel: null },
  { id: 'va_bni', name: 'VA BNI', icon: Building2, channel: 'bni' },
  { id: 'va_bri', name: 'VA BRI', icon: Building2, channel: 'bri' },
  { id: 'va_mandiri', name: 'VA Mandiri', icon: Building2, channel: 'mandiri' },
  { id: 'va_cimb', name: 'VA CIMB', icon: Building2, channel: 'cimb' },
];

const BANK_INFO = {
  bni: { name: 'BNI', color: '#f15a22' },
  bri: { name: 'BRI', color: '#00529c' },
  mandiri: { name: 'Mandiri', color: '#003366' },
  cimb: { name: 'CIMB', color: '#ec1c24' },
};

export default function Pascabayar() {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedSku, setSelectedSku] = useState(null);
  const [customerNo, setCustomerNo] = useState('');
  const [email, setEmail] = useState('');
  const [topupAmount, setTopupAmount] = useState('');

  // Inquiry state
  const [inquiring, setInquiring] = useState(false);
  const [billData, setBillData] = useState(null);

  // Payment state
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paying, setPaying] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [step, setStep] = useState('select'); // select | inquiry | pay | paying | success

  useEffect(() => { fetchCatalog(); }, []);

  // E-Money / direct pay brands don't need inquiry
  const isDirectPay = selectedBrand?.brand === 'E-MONEY';

  const handleDirectPay = () => {
    if (!selectedSku) return toast.error('Pilih layanan');
    if (!customerNo) return toast.error('Masukkan nomor HP');
    if (!topupAmount || Number(topupAmount) < 10000) return toast.error('Minimal top-up Rp 10.000');
    setBillData({
      customer_name: '',
      selling_price: Number(topupAmount),
      price: Number(topupAmount),
      admin: 0,
    });
    setStep('pay');
  };

  const fetchCatalog = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/biller/postpaid/catalog`);
      if (res.data.success) setBrands(res.data.brands || []);
    } catch {} finally { setLoading(false); }
  };

  const handleInquiry = async () => {
    if (!selectedSku) return toast.error('Pilih jenis tagihan');
    if (!customerNo) return toast.error('Masukkan nomor pelanggan');
    setInquiring(true);
    try {
      const res = await axios.post(`${API_URL}/api/biller/postpaid/inquiry`, {
        buyer_sku_code: selectedSku.sku,
        customer_no: customerNo,
      });
      if (res.data.success) {
        setBillData({...res.data, inquiry_ref_id: res.data.ref_id});
        setStep('pay');
        toast.success('Tagihan ditemukan');
      } else {
        toast.error(res.data.error || 'Tagihan tidak ditemukan');
      }
    } catch (e) {
      toast.error('Gagal cek tagihan');
    } finally { setInquiring(false); }
  };

  const handlePay = async () => {
    if (!selectedPayment) return toast.error('Pilih metode pembayaran');
    if (!email) return toast.error('Masukkan email');
    setPaying(true);
    try {
      // Create order
      const orderRes = await axios.post(`${API_URL}/api/orders/digiflazz`, {
        brand: selectedBrand?.brand || 'Pascabayar',
        sku_code: selectedSku.sku,
        product_name: selectedSku.name,
        game_user_id: customerNo,
        email,
        payment_method: selectedPayment.id,
        price: billData.selling_price || billData.price || 0,
      });
      const orderNumber = orderRes.data.order.order_number;

      // Create Ayolinx payment (pass inquiry_ref_id for postpaid)
      const amount = billData.selling_price || billData.price || 0;
      const isQris = selectedPayment.id === 'qris';
      const payRes = await axios.post(`${API_URL}/api/payment/create`, {
        order_id: orderNumber,
        amount,
        customer_name: billData.customer_name || email.split('@')[0],
        customer_email: email,
        payment_method: isQris ? 'qris' : 'va',
        va_channel: selectedPayment.channel || 'bni',
        item_name: `${selectedBrand?.brand} - ${selectedSku.name}`,
        digiflazz_sku: selectedSku.sku,
        customer_game_id: customerNo,
        inquiry_ref_id: billData.inquiry_ref_id || null,
      });

      if (payRes.data.success) {
        setPaymentData({ ...payRes.data.data, orderId: orderNumber, amount });
        setStep('paying');
        toast.success('Silakan selesaikan pembayaran');
      } else {
        toast.error(payRes.data.message || 'Gagal membuat pembayaran');
      }
    } catch (e) {
      toast.error('Gagal memproses pembayaran');
    } finally { setPaying(false); }
  };

  // Poll payment status
  useEffect(() => {
    if (step !== 'paying' || !paymentData?.orderId) return;
    let active = true;
    const poll = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/payment/status/${paymentData.orderId}`);
        if (!active) return;
        if (res.data.status === 'paid' || res.data.status === 'completed') {
          setStep('success');
          return;
        }
        if (res.data.status === 'failed' || res.data.status === 'cancelled') {
          toast.error('Pembayaran gagal atau dibatalkan');
          setStep('pay');
          return;
        }
      } catch {}
      if (active) setTimeout(poll, 4000);
    };
    setTimeout(poll, 4000);
    return () => { active = false; };
  }, [step, paymentData?.orderId]);

  const copyText = (t) => { navigator.clipboard.writeText(t); toast.success('Disalin!'); };

  const formatPrice = (p) => `Rp ${Number(p || 0).toLocaleString('id-ID')}`;

  if (loading) {
    return <div className="min-h-screen pt-24 flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
    </div>;
  }

  // ==================== SUCCESS ====================
  if (step === 'success') {
    return (
      <div className="min-h-screen pt-24 pb-12">
        <div className="max-w-lg mx-auto px-4 text-center">
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="font-rajdhani font-bold text-2xl text-white uppercase mb-2">Pembayaran Berhasil!</h1>
          <p className="text-muted-foreground mb-6">Tagihan sedang diproses oleh provider.</p>

          <div className="bg-card rounded-xl p-6 border border-border text-left mb-6">
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Jenis Tagihan</span>
                <span className="text-white">{selectedSku?.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">No. Pelanggan</span>
                <span className="font-mono text-white">{customerNo}</span>
              </div>
              {billData?.customer_name && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Nama</span>
                  <span className="text-white">{billData.customer_name}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Status</span>
                <span className="text-green-500 font-medium">Berhasil</span>
              </div>
            </div>
            <div className="border-t border-border mt-4 pt-4 flex justify-between items-center">
              <span className="text-gray-400">Total Bayar</span>
              <span className="font-mono text-xl font-bold text-green-500">{formatPrice(paymentData?.amount)}</span>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1 border-border text-white" onClick={() => navigate('/track')}>Cek Transaksi</Button>
            <Button className="flex-1 bg-primary hover:bg-primary/90 text-white" onClick={() => navigate('/')}>Kembali</Button>
          </div>
        </div>
      </div>
    );
  }

  // ==================== PAYING ====================
  if (step === 'paying' && paymentData) {
    const isVA = paymentData.payment_method === 'virtual_account';
    return (
      <div className="min-h-screen pt-24 pb-12">
        <div className="max-w-lg mx-auto px-4">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-accent" />
            </div>
            <h1 className="font-rajdhani font-bold text-2xl text-white uppercase mb-2">Menunggu Pembayaran</h1>
            <p className="text-muted-foreground">{selectedSku?.name} — {formatPrice(paymentData.amount)}</p>
          </div>

          {isVA ? (
            <div className="bg-card rounded-xl p-6 border border-border mb-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1.5 rounded font-bold text-sm text-white"
                  style={{ backgroundColor: BANK_INFO[paymentData.channel]?.color || '#333' }}>
                  {BANK_INFO[paymentData.channel]?.name || paymentData.channel?.toUpperCase()}
                </span>
                <span className="text-white">Virtual Account</span>
              </div>
              <div className="bg-black/30 rounded-lg p-4 mb-4">
                <p className="text-xs text-muted-foreground mb-2">Nomor Virtual Account</p>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xl text-white font-bold tracking-wider">{paymentData.va_number}</span>
                  <button onClick={() => copyText(paymentData.va_number)} className="p-2 rounded-lg bg-primary/20 text-primary hover:bg-primary/30">
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-card rounded-xl p-6 border border-border mb-6">
              <div className="flex items-center gap-3 mb-4">
                <QrCode className="w-6 h-6 text-secondary" />
                <span className="font-medium text-white">QRIS</span>
              </div>
              <div className="bg-white rounded-xl p-6 mb-4 flex justify-center">
                {paymentData.qr_content ? (
                  <QRCodeCanvas value={paymentData.qr_content} size={220} level="M" marginSize={2} />
                ) : <QrCode className="w-32 h-32 text-gray-300" />}
              </div>
              <p className="text-center text-sm text-muted-foreground">Scan QR code dengan e-wallet atau mobile banking</p>
            </div>
          )}

          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4 text-center">
            <p className="text-xs text-yellow-400 flex items-center justify-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Jangan tinggalkan halaman ini
            </p>
          </div>

          <Button variant="outline" className="w-full border-border text-muted-foreground" onClick={() => setStep('pay')}>
            <RefreshCw className="w-4 h-4 mr-2" /> Ganti Metode Pembayaran
          </Button>
        </div>
      </div>
    );
  }

  // ==================== MAIN FORM ====================
  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" size="sm" className="text-white mb-6" onClick={() => navigate('/')}>
          <ChevronLeft className="w-4 h-4 mr-1" /> Kembali
        </Button>

        <div className="text-center mb-8">
          <Receipt className="w-12 h-12 text-primary mx-auto mb-4" />
          <h1 className="font-rajdhani font-bold text-3xl text-white uppercase">Bayar Tagihan</h1>
          <p className="text-muted-foreground mt-2">PLN Pascabayar, BPJS, Internet, HP Pascabayar, dan lainnya</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">

            {/* Step 1: Pilih jenis tagihan */}
            <div className="bg-card rounded-xl p-6 border border-border">
              <h2 className="font-rajdhani font-semibold text-lg text-white uppercase mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" /> 1. Pilih Jenis Tagihan
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {brands.map(b => (
                  <button key={b.slug} onClick={() => { setSelectedBrand(b); setSelectedSku(null); setBillData(null); setStep('select'); }}
                    className={`p-3 rounded-xl text-left border transition-all ${selectedBrand?.slug === b.slug ? 'border-primary bg-primary/10' : 'border-white/10 bg-black/20 hover:border-white/30'}`}>
                    <p className="text-white font-medium text-sm">{b.brand}</p>
                    <p className="text-xs text-muted-foreground mt-1">{b.items.length} layanan</p>
                  </button>
                ))}
              </div>

              {selectedBrand && (
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-sm text-gray-400 mb-3">Pilih Layanan:</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {selectedBrand.items.filter(i => i.active).map(item => (
                      <button key={item.sku} onClick={() => { setSelectedSku(item); setBillData(null); setStep('select'); }}
                        className={`p-3 rounded-lg text-left border transition-all ${selectedSku?.sku === item.sku ? 'border-primary bg-primary/10' : 'border-white/10 hover:border-white/30'}`}>
                        <p className="text-white text-sm">{item.name}</p>
                        <p className="text-xs text-muted-foreground font-mono">{item.sku}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Step 2: Nomor pelanggan */}
            <div className="bg-card rounded-xl p-6 border border-border">
              <h2 className="font-rajdhani font-semibold text-lg text-white uppercase mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-primary" /> 2. {isDirectPay ? 'Data Top-up' : 'Nomor Pelanggan'}
              </h2>
              <div className="space-y-3">
                <div className="flex gap-3">
                  <Input placeholder={isDirectPay ? 'Nomor HP terdaftar' : 'Masukkan nomor pelanggan'}
                    className="bg-black/50 border-white/10 text-white font-mono flex-1"
                    value={customerNo} onChange={(e) => setCustomerNo(e.target.value)} data-testid="pasca-customer-no" />
                  {!isDirectPay && (
                    <Button className="bg-primary hover:bg-primary/90 text-white px-6" onClick={handleInquiry}
                      disabled={inquiring || !selectedSku || !customerNo} data-testid="pasca-inquiry-btn">
                      {inquiring ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-2" /> Cek Tagihan</>}
                    </Button>
                  )}
                </div>
                {isDirectPay && (
                  <div>
                    <Label className="text-gray-400 text-sm mb-2 block">Nominal Top-up</Label>
                    <Input type="number" placeholder="Contoh: 50000 (min. 10.000)"
                      className="bg-black/50 border-white/10 text-white font-mono"
                      value={topupAmount} onChange={(e) => setTopupAmount(e.target.value)} data-testid="pasca-amount" />
                    <Button className="bg-primary hover:bg-primary/90 text-white w-full mt-3" onClick={handleDirectPay}
                      disabled={!selectedSku || !customerNo || !topupAmount}>
                      <Zap className="w-4 h-4 mr-2" /> Lanjutkan
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Step 3: Detail tagihan (after inquiry) */}
            {step === 'pay' && billData && (
              <>
                <div className="bg-card rounded-xl p-6 border border-green-500/30">
                  <h2 className="font-rajdhani font-semibold text-lg text-white uppercase mb-4 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-500" /> 3. Detail Tagihan
                  </h2>
                  <div className="space-y-3">
                    {billData.customer_name && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Nama Pelanggan</span>
                        <span className="text-white font-medium">{billData.customer_name}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">No. Pelanggan</span>
                      <span className="font-mono text-white">{customerNo}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Layanan</span>
                      <span className="text-white">{selectedSku?.name}</span>
                    </div>
                    {billData.desc && typeof billData.desc === 'object' && Object.entries(billData.desc).map(([k, v]) => (
                      <div key={k} className="flex justify-between text-sm">
                        <span className="text-gray-400 capitalize">{k.replace(/_/g, ' ')}</span>
                        <span className="text-white">{String(v)}</span>
                      </div>
                    ))}
                    {billData.admin > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Biaya Admin</span>
                        <span className="text-white">{formatPrice(billData.admin)}</span>
                      </div>
                    )}
                    <div className="border-t border-border pt-3 flex justify-between items-center">
                      <span className="text-gray-300 font-medium">Total Bayar</span>
                      <span className="font-mono text-xl font-bold text-primary">{formatPrice(billData.selling_price || billData.price)}</span>
                    </div>
                  </div>
                </div>

                {/* Step 4: Payment method */}
                <div className="bg-card rounded-xl p-6 border border-border">
                  <h2 className="font-rajdhani font-semibold text-lg text-white uppercase mb-4">4. Metode Pembayaran</h2>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {PAYMENT_METHODS.map(m => {
                      const Icon = m.icon;
                      return (
                        <button key={m.id} onClick={() => setSelectedPayment(m)}
                          className={`payment-card p-3 rounded-xl flex flex-col items-center gap-2 ${selectedPayment?.id === m.id ? 'selected' : ''}`}>
                          <Icon className="w-5 h-5 text-secondary" />
                          <span className="text-xs font-medium text-white">{m.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Step 5: Email */}
                <div className="bg-card rounded-xl p-6 border border-border">
                  <h2 className="font-rajdhani font-semibold text-lg text-white uppercase mb-4">5. Email</h2>
                  <Input type="email" placeholder="Masukkan email" className="bg-black/50 border-white/10 text-white"
                    value={email} onChange={(e) => setEmail(e.target.value)} data-testid="pasca-email" />
                </div>
              </>
            )}
          </div>

          {/* Sidebar summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-card rounded-xl p-6 border border-border">
              <h2 className="font-rajdhani font-semibold text-lg text-white uppercase mb-4">Ringkasan</h2>
              {selectedSku ? (
                <div className="space-y-3 mb-6">
                  <div className="bg-black/30 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground">Jenis Tagihan</p>
                    <p className="text-white font-medium">{selectedBrand?.brand}</p>
                    <p className="text-sm text-primary">{selectedSku.name}</p>
                  </div>
                  {customerNo && (
                    <div className="bg-black/30 rounded-lg p-3 font-mono text-sm">
                      <p className="text-gray-300">No: <span className="text-white">{customerNo}</span></p>
                    </div>
                  )}
                  {billData?.customer_name && (
                    <div className="bg-black/30 rounded-lg p-3 text-sm">
                      <p className="text-gray-300">Nama: <span className="text-white">{billData.customer_name}</span></p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm mb-6">Pilih jenis tagihan terlebih dahulu</p>
              )}

              <div className="border-t border-border pt-4 mb-6 flex justify-between items-center">
                <span className="text-gray-300">Total</span>
                <span className="font-mono text-xl font-bold text-white">
                  {billData ? formatPrice(billData.selling_price || billData.price) : 'Rp 0'}
                </span>
              </div>

              {step === 'pay' ? (
                <Button className="w-full bg-primary hover:bg-primary/90 text-white font-rajdhani uppercase py-6"
                  onClick={handlePay} disabled={paying || !selectedPayment || !email} data-testid="pasca-pay-btn">
                  {paying ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Memproses...</> :
                    <><Zap className="w-4 h-4 mr-2" /> Bayar Sekarang</>}
                </Button>
              ) : isDirectPay ? (
                <Button className="w-full bg-primary hover:bg-primary/90 text-white font-rajdhani uppercase py-6"
                  onClick={handleDirectPay} disabled={!selectedSku || !customerNo || !topupAmount}>
                  <Zap className="w-4 h-4 mr-2" /> Lanjutkan
                </Button>
              ) : (
                <Button className="w-full bg-primary hover:bg-primary/90 text-white font-rajdhani uppercase py-6"
                  onClick={handleInquiry} disabled={inquiring || !selectedSku || !customerNo} data-testid="pasca-check-btn">
                  {inquiring ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Mengecek...</> :
                    <><Search className="w-4 h-4 mr-2" /> Cek Tagihan</>}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
